@let createdId = createdRoomId();

<div class="room-dialog">
  <div class="dialog-header">
    <div class="title">
      @if (createdId) {
        <fa-duotone-icon class="title__success" [icon]="faCircleCheck"></fa-duotone-icon>
        <span>{{ 'Room created' | translate }}</span>
      } @else {
        <fa-duotone-icon [icon]="isEdit ? faPenToSquare : faSquarePlus"></fa-duotone-icon>
        <span>{{ (isEdit ? 'Edit room' : 'Create a room') | translate }}</span>
      }
    </div>
    <button
      type="button"
      class="close-btn"
      [attr.aria-label]="'Close' | translate"
      [andorTooltip]="'Close' | translate"
      tooltipPosition="below"
      (click)="close()">
      <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
    </button>
  </div>

  @if (createdId) {
    <div class="confirmation">
      <p class="confirmation__lead">
        {{ '"[name]" is ready to share.' | translate: { name: createdName() } }}
      </p>

      <button
        type="button"
        class="invite-link"
        [andorTooltip]="'Copy invite link' | translate"
        [attr.aria-label]="'Copy invite link' | translate"
        (click)="copyInviteLink()">
        <fa-duotone-icon [icon]="faLink"></fa-duotone-icon>
        <span class="invite-link__url">{{ inviteLink() }}</span>
        @if (copied()) {
          <fa-icon class="invite-link__state" [icon]="faCheck"></fa-icon>
        } @else {
          <fa-icon class="invite-link__state" [icon]="faCopy"></fa-icon>
        }
      </button>

      @if (createdPassword()) {
        <andor-text-field
          [formControl]="passwordControl"
          [readonly]="true"
          [type]="passwordVisible() ? 'text' : 'password'"
          [inlineActions]="passwordActions()"
          [labelTooltip]="
            'Anyone joining this room needs this password. It is part of the invite link, so it only has to be shared separately when you send the room name instead of the link.'
              | translate
          "
          [label]="'Password' | translate"></andor-text-field>
      }
    </div>

    <div class="actions">
      <andor-button type="gradient" (click)="close()">
        <span>{{ 'Done' | translate }}</span>
      </andor-button>
    </div>
  } @else {
    <div class="form" (andorSubmit)="submit()" [andorSubmitDisabled]="submitDisabled()">
      <div class="entry">
        <andor-text-field
          [formControl]="createNameControl"
          [placeholder]="'Desired Room' | translate"
          [labelTooltip]="'The name of the room you want to create.' | translate"
          [errorMessages]="{
            required: 'Room name is required.' | translate,
            minlength: 'Room name must be at least 3 characters long.' | translate,
          }"
          [inlineActions]="nameGeneratorActions"
          [label]="'Room Name' | translate"></andor-text-field>
      </div>

      <div class="entry">
        <andor-mat-toggle-wrapper
          [label]="'Private room' | translate"
          [tooltipText]="
            'Regular users must be approved by a moderator before they can join; moderators join directly. The room still appears in the list with a lock icon — it\'s gated, not hidden or password-protected.'
              | translate
          ">
          <mat-slide-toggle
            [checked]="isPrivate()"
            (change)="isPrivate.set($event.checked)"></mat-slide-toggle>
        </andor-mat-toggle-wrapper>
      </div>

      <div class="entry">
        <andor-mat-toggle-wrapper
          [label]="'Schedule this meeting' | translate"
          [tooltipText]="
            'Sets the meeting\'s time window. Outside it, regular users need a moderator to admit them early; during the window they join normally. The room is created now — it isn\'t automatically opened or closed at these times.'
              | translate
          ">
          <mat-slide-toggle
            [checked]="scheduleEnabled()"
            (change)="scheduleEnabled.set($event.checked)"></mat-slide-toggle>
        </andor-mat-toggle-wrapper>
      </div>

      @if (scheduleEnabled()) {
        <div class="entry">
          <andor-datetime-field
            [value]="startMs()"
            (valueChange)="startMs.set($event)"
            [min]="startMin"
            [label]="'Start' | translate"></andor-datetime-field>
        </div>
        <div class="entry">
          <andor-datetime-field
            [value]="endMs()"
            (valueChange)="endMs.set($event)"
            [min]="endMin()"
            [label]="'End' | translate"></andor-datetime-field>
          <span class="time-zone-hint">
            {{ 'Time zone: [zone]' | translate: { zone: timeZone } }}
          </span>
        </div>
        <div class="entry">
          <andor-select-field
            [title]="'Repeat' | translate"
            [options]="frequencyOptions"
            [selectedOption]="selectedFrequency()"
            (selected)="selectedFrequency.set($event)"></andor-select-field>
        </div>
        @if (selectedFrequency().id !== 'none') {
          <div class="entry entry--interval">
            <andor-text-field
              type="number"
              [formControl]="intervalControl"
              [label]="'Every' | translate"></andor-text-field>
            <span class="interval-unit">{{ intervalUnitKey() | translate }}</span>
          </div>
        }
      }

      <div class="entry">
        <andor-e2e-encryption-panel
          [roomName]="room?.roomId ?? ''"
          [mode]="isEdit ? 'readonly' : 'auto'"
          [defaultEncrypted]="!isEdit" />
      </div>
    </div>

    <div class="actions">
      <andor-button type="gradient" (click)="submit()" [disabled]="submitDisabled()">
        <span>{{ (isEdit ? 'Save' : 'Create') | translate }}</span>
      </andor-button>
    </div>
  }
</div>
